import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { JobRole } from './onboarding.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { TrackerStep } from '@app/onboarding/components/tracker/tracker.component';
import { Skill } from '@app/onboarding/onboarding.model';
import { OnboardingStep } from '@app/onboarding/onboarding.enums';
import { ResumeResult } from '@app/onboarding/onboarding-api.model';

@Injectable({
  providedIn: 'root',
})
export class OnboardingStateService {
  private currentStepSubject = new BehaviorSubject<OnboardingStep>(
    OnboardingStep.welcome
  );

  private currentRoleSubject = new BehaviorSubject<JobRole>(undefined);
  private featuredTargetTilesSubject = new BehaviorSubject<JobRole[]>(
    undefined
  );
  private assignedTagsSubject = new BehaviorSubject<TagsApi.Tag[]>(undefined);
  private similarTagsSubject = new BehaviorSubject<Skill[]>([]);
  private displayedSimilarTagsSubject = new BehaviorSubject<Skill[]>([]);
  private trackerStepsSubject = new BehaviorSubject<TrackerStep[]>(undefined);
  private futureTrackerSubject = new BehaviorSubject<TrackerStep[]>(undefined);
  private resumeSubject = new BehaviorSubject<ResumeResult>(undefined);
  private profileSubject = new BehaviorSubject<Skill[]>([]);
  private isDs3: boolean = false;
  private addedTagsSubject = new BehaviorSubject<Skill[]>([]);
  private onboardingUserAddedTagsSubject = new BehaviorSubject<Skill[]>([]);

  constructor() {}

  // user skills added during onboarding process for assigned role skills and resume skills
  public get onboardingUserAddedTags$(): Observable<Skill[]> {
    return this.onboardingUserAddedTagsSubject.asObservable();
  }
  public get onboardingUserAddedTags(): Skill[] {
    return this.onboardingUserAddedTagsSubject.getValue();
  }

  // user skills added fron search and suggested on review skills page
  public get addedTags$(): Observable<Skill[]> {
    return this.addedTagsSubject.asObservable();
  }
  public get addedTags(): Skill[] {
    return this.addedTagsSubject.getValue();
  }

  // Current Step the user is on
  public get currentStep(): OnboardingStep {
    return this.currentStepSubject.getValue();
  }
  public get currentStep$(): Observable<OnboardingStep> {
    return this.currentStepSubject.asObservable();
  }

  // Current role user has selected
  public get currentRole(): JobRole {
    return this.currentRoleSubject.getValue();
  }
  public get currentRole$(): Observable<JobRole> {
    return this.currentRoleSubject.asObservable();
  }
  // Roles displayed on role selection
  public get featuredRoleTargets(): JobRole[] {
    return this.featuredTargetTilesSubject.getValue();
  }

  public get featuredRoleTargets$(): Observable<JobRole[]> {
    return this.featuredTargetTilesSubject.asObservable();
  }

  // assigned skills from the current role
  public get assignedTags(): TagsApi.Tag[] {
    return this.assignedTagsSubject.getValue();
  }

  public get assignedTags$(): Observable<TagsApi.Tag[]> {
    return this.assignedTagsSubject.asObservable();
  }

  // Current tracker (dots) steps
  public get trackerSteps(): TrackerStep[] {
    return this.trackerStepsSubject.getValue();
  }

  public get trackerSteps$(): Observable<TrackerStep[]> {
    return this.trackerStepsSubject.asObservable();
  }

  // used in the role page to update the traker after going to the next page
  // don't want the dot to appear/dispear while cliking on roles
  public get futureTrackerStates(): TrackerStep[] {
    return this.futureTrackerSubject.getValue();
  }

  // List of similar tags from skills similarity ds3 service
  public get similarTags(): Skill[] {
    return this.similarTagsSubject.getValue();
  }

  public get similarTags$(): Observable<Skill[]> {
    return this.similarTagsSubject.asObservable();
  }
  // List of skills displayed
  public get displayedSimilarTags(): Skill[] {
    return this.displayedSimilarTagsSubject.getValue();
  }

  public get displayedSimilarTags$(): Observable<Skill[]> {
    return this.displayedSimilarTagsSubject.asObservable();
  }

  // Users profile skills
  public get currentProfileSkills(): Skill[] {
    return this.profileSubject.getValue();
  }
  public get currentProfileSkills$(): Observable<Skill[]> {
    return this.profileSubject.asObservable();
  }

  public get isSimilarService(): boolean {
    return this.isDs3;
  }

  // current resume
  public get currentResume(): ResumeResult {
    return this.resumeSubject.getValue();
  }
  public get currentResume$(): Observable<ResumeResult> {
    return this.resumeSubject.asObservable();
  }

  public updateResume(resume: ResumeResult): void {
    this.resumeSubject.next(resume);
  }

  public setIsSimilarService(isDs3: boolean) {
    this.isDs3 = isDs3;
  }

  public updateProfileSkills(tags: Skill[]): void {
    this.profileSubject.next(tags);
  }
  public addProfileSkill(skill: Skill) {
    this.profileSubject.getValue().push(skill);
  }

  public currentStepChange(step: OnboardingStep): void {
    this.currentStepSubject.next(step);
  }

  public addSkillsChange(skills: Skill[]): void {
    this.addedTagsSubject.next(skills);
  }

  public onboardingUserAddedTagsChange(skills: Skill[]): void {
    this.onboardingUserAddedTagsSubject.next(skills);
  }

  public currentRoleChange(role: JobRole): void {
    this.currentRoleSubject.next(role);
    // map the roles for the selected to all be selected on role change
    role?.tags.forEach((tag) => {
      tag.isFollowing = true;
    });

    this.assignedTagsChange(role?.tags ? role.tags : []);
  }

  public featuredRoleTargetsChange(roles: JobRole[]): void {
    this.featuredTargetTilesSubject.next(roles);
  }

  public assignedTagsChange(tags: TagsApi.Tag[]): void {
    this.assignedTagsSubject.next(tags);
  }

  public updateTrackerStep(onboardingStepStates: TrackerStep[]): void {
    this.trackerStepsSubject.next(onboardingStepStates);
  }

  /**
   *  Set the next footer button state to the stream based on the page the user is on
   *  @param onboardingStepStates:
   */
  public futureTrackerStateChange(onboardingStepStates: TrackerStep[]): void {
    this.futureTrackerSubject.next(onboardingStepStates);
  }

  /**
   * Update all the similar list tags
   * @param tags to update the similar skills list too
   */
  public updateSimilarTags(tags: Skill[]): void {
    this.similarTagsSubject.next(tags);
  }

  /**
   * Update all the displayed similar skills  tags
   * @param tags to update the displayed similar skills list too
   */
  public updateDisplayedSimilarTags(tags: Skill[]): void {
    this.displayedSimilarTagsSubject.next(tags);
  }

  /**
   * Add multiple skills to the end of the displayed similar skills list
   * @param skills skills to add to the displayed similar skills list
   */
  public addDisplayedSimilarSkills(skills: Skill[]) {
    const skillList: Skill[] = !!this.displayedSimilarTags
      ? this.displayedSimilarTags
      : [];

    this.updateDisplayedSimilarTags([...skillList, ...skills]);
  }
}
